import { SW_BRAND } from './config';

/**
 * Brand-free copy for the airline help desk page — no airline names,
 * loyalty programmes, or fare-bundle names.
 */

export const META = {
  title: `Flight Reservations Help Desk | ${SW_BRAND}`,
  description: `Flight reservations help desk by ${SW_BRAND}, an independent travel agency. Compare fares, understand baggage and change rules, and get 24/7 booking support.`,
};

export const HERO = {
  badge: '24/7 Flight Booking Support',
  titleLead: 'Flight Reservations',
  title: 'Help Desk',
  chips: [
    { icon: 'deals', label: 'Compare Fares', sub: 'Dates, routes and fare rules' },
    { icon: 'help', label: 'Best Travel Assistance', sub: '24/7 Support' },
    { icon: 'book', label: 'Booking, Charges, Cancellation', sub: 'Flexible Policies' },
  ],
};

export const ARTICLE = {
  title: 'Airline reservations and changes Help Desk',
  intro:
    'Booking a flight, changing a date, or sorting out a cancellation is easier with clear information. This guide covers how airline baggage rules, routes, loyalty programmes, and change policies generally work, so you know what to check before you book or modify a trip.',
  sections: [
    {
      id: 'swp-baggage',
      heading: 'Airline baggage policy',
      paragraphs: [
        'Baggage allowances depend on the airline, the route, and the fare type you buy. Lower-priced fares often include only a personal item, while standard and premium fares usually add a carry-on bag and one or more checked bags. Always confirm the current allowance for your ticket before you travel.',
      ],
    },
    {
      id: 'swp-routes',
      heading: 'Popular airline routes and destinations',
      paragraphs: ['Some of the most frequently searched city pairs include:'],
      bullets: [
        'New York (JFK) – Los Angeles (LAX): a busy coast-to-coast route',
        'Orlando (MCO) – Atlanta (ATL): popular for family vacations',
        'Chicago (ORD) – Denver (DEN): a busy business and leisure route',
        'Dallas (DFW) – Las Vegas (LAS): a favourite for short getaways',
        'International trips to Cancun, London, Paris, and the Caribbean',
      ],
    },
    {
      id: 'swp-rewards',
      heading: 'Airline loyalty programmes',
      paragraphs: [
        'Most airlines run a loyalty programme where you earn miles or points on flights you pay for. Points can usually be redeemed for future flights or upgrades, and frequent travellers can reach elite status for perks such as priority boarding and extra baggage.',
      ],
    },
    {
      id: 'swp-tips',
      heading: 'Booking tips for cheaper airline tickets',
      paragraphs: ['These habits often help travellers secure a better fare:'],
      bullets: [
        'Compare midweek departures, which are often priced lower',
        'Use a fare calendar to find the cheapest travel dates',
        'Sign up for fare alerts to track price drops',
        `Book through ${SW_BRAND} to compare prices and get 24/7 support`,
        'Check what your fare includes before choosing the lowest price',
      ],
    },
    {
      id: 'swp-changes',
      heading: 'Airline flight change and cancellation policy',
      paragraphs: [
        'Many standard airline fares can be changed without a change fee — you pay only the difference in fare. Basic or promotional fares are often more restrictive. Cancelled tickets may be refunded or returned as travel credit depending on the fare rules, so review your ticket conditions before making changes.',
      ],
    },
    {
      id: 'swp-contact',
      heading: `Airline booking help with ${SW_BRAND}`,
      paragraphs: [
        `When you book your airline reservations through ${SW_BRAND}, you get more than a flight booking. You can compare prices across dates, find the best deals, and book with confidence. Our support team is available 24/7 to help with changes, cancellations, or any questions about your trip.`,
      ],
    },
  ],
};

export const FAQ_INTRO =
  'Get answers to common questions about airline flights, baggage, and policies';

export const FAQS = [
  {
    q: 'What is the airline baggage policy?',
    a: 'Baggage allowances depend on the airline, route, and fare type. Many fares include a carry-on bag, while checked bags may be included or charged separately. Check your ticket conditions before you fly.',
  },
  {
    q: 'How do I check in for my flight?',
    a: 'Most airlines open online check-in 24 hours before departure. You can also check in at airport kiosks or the ticket counter, and mobile boarding passes are widely available.',
  },
  {
    q: 'Can I change or cancel my flight?',
    a: 'Most standard fares can be changed or cancelled, sometimes with a fee and always subject to any fare difference. Basic fares are often more restrictive. Our team can help you review your options.',
  },
  {
    q: 'Which destinations can I book?',
    a: 'You can search domestic and international flights to hundreds of destinations, including popular cities across the United States, Mexico, the Caribbean, Europe, and Asia.',
  },
  {
    q: 'Do airlines have loyalty programmes?',
    a: 'Yes, most airlines offer a loyalty programme where you earn miles or points on flights and redeem them for future travel, upgrades, and other benefits.',
  },
  {
    q: 'How do I choose or change my seat?',
    a: 'Seat selection rules vary by fare. Some fares include free seat selection, while others charge a fee or assign seats at check-in.',
  },
  {
    q: 'Can I book international flights?',
    a: 'Yes, you can compare and book international flights. Make sure your passport and any required visas are valid for your travel dates.',
  },
  {
    q: 'How do I get help with my booking?',
    a: `${SW_BRAND} provides 24/7 support for reservations, changes, and cancellations. Call the number on this page to speak with our team.`,
  },
];
